<template>
  <div class="catalog">
    <Navbar></Navbar>

    <b-container>
      <b-row class="pt-5 pb-2">
        <b-col xs="0" sm="0" md="2" lg="2" xl="2"></b-col>
        <b-col>
          <div class="text-center">
            <h1>Catalog</h1>

            <p class="lead">
              Browse Halloween and Christmas movie schedules in one place.
              Search by title, channel, or holiday, and filter listings by date
              and location to find your next festive movie night.
            </p>
          </div>
        </b-col>
        <b-col xs="0" sm="0" md="2" lg="2" xl="2"></b-col>
      </b-row>

      <b-row class="pb-5">
        <!-- Statistics -->
        <b-col>
          <div class="border-top border-bottom py-3">
            <b-row class="align-items-center">
              <b-col cols="12" md="3" class="mb-3 mb-md-0">
                <div class="text-muted small">TOTAL LISTINGS</div>
                <div class="h3 mb-0" v-if="isLoading">
                  <b-spinner class="align-middle" aria-label="Loading" small></b-spinner>
                </div>
                <div class="h3 mb-0" v-else-if="fetchFailed">Err</div>
                <div class="h3 mb-0" v-else>{{ catalogStats.total }}</div>
              </b-col>
              <b-col cols="6" md="3">
                <div class="text-muted small">CHRISTMAS</div>
                <div class="h3 mb-0" v-if="isLoading">
                  <b-spinner class="align-middle" aria-label="Loading" small></b-spinner>
                </div>
                <div class="h3 mb-0" v-else-if="fetchFailed">Err</div>
                <div class="h3 mb-0" v-else>{{ catalogStats.christmas }}</div>
              </b-col>
              <b-col cols="6" md="3">
                <div class="text-muted small">HALLOWEEN</div>
                <div class="h3 mb-0" v-if="isLoading">
                  <b-spinner class="align-middle" aria-label="Loading" small></b-spinner>
                </div>
                <div class="h3 mb-0" v-else-if="fetchFailed">Err</div>
                <div class="h3 mb-0" v-else>{{ catalogStats.halloween }}</div>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>

      <b-row>
        <!-- Search Bar -->
        <b-col cols="11">
          <b-input-group class="mb-3">
            <b-button
              type="button"
              variant="danger"
              aria-label="Clear search"
              :disabled="!hasMedia"
              @click="clearSearch"
            >
              <font-awesome-icon class="theme-icon" :icon="['fas', 'times']" />
            </b-button>
            <b-form-input
              ref="searchInput"
              v-model="searchQuery"
              placeholder="Search movies..."
              :disabled="!hasMedia"
              @keyup.enter="searchQuery = searchQuery.trim()"
            ></b-form-input>
            <b-button
              variant="primary"
              aria-label="Search movies"
              :disabled="!hasMedia"
              @click="searchQuery = searchQuery.trim()"
            >
              <font-awesome-icon class="theme-icon" :icon="['fas', 'search']" />
            </b-button>
          </b-input-group>
        </b-col>

        <!-- Filter Section -->
        <b-col cols="1">
          <b-button
            class="w-100"
            variant="secondary"
            :disabled="!hasMedia"
            v-b-toggle.filter-collapse
          >
            <font-awesome-icon class="theme-icon" :icon="['fas', 'filter']" />
          </b-button>
        </b-col>
      </b-row>

      <!-- Collapse Section -->
      <b-collapse id="filter-collapse" class="mt-1 mb-4">
        <b-card>
          <b-row>
            <b-col md="2">
              <b-form-group label="Type">
                <b-form-checkbox v-model="filters.halloween">&nbsp;&nbsp;Halloween</b-form-checkbox>
                <b-form-checkbox v-model="filters.christmas">&nbsp;&nbsp;Christmas</b-form-checkbox>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Date Range">
                <b-row>
                  <b-col cols="6">
                    <b-form-datepicker v-model="filters.startDate" placeholder="Start Date"></b-form-datepicker>
                  </b-col>
                  <b-col cols="6">
                    <b-form-datepicker v-model="filters.endDate" placeholder="End Date"></b-form-datepicker>
                  </b-col>
                </b-row>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Location">
                <b-form-select v-model="filters.location" :options="locationOptions"></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
      </b-collapse>

      <b-row>
        <!-- Movie Schedule Table -->
        <b-col cols="12">
          <b-card no-body class="overflow-hidden">
            <b-table
              class="mb-0 table-responsive"
              :dark="isDarkMode"
              borderless
              striped
              responsive
              hover
              show-empty
              :busy="isLoading"
              :items="filteredTableItems"
              :fields="tableFields"
            >
              <template #table-busy>
                <div class="text-center py-4">
                  <b-spinner class="align-middle" aria-label="Loading"></b-spinner>
                  <!-- <span class="ml-2">Loading movie schedules...</span> -->
                </div>
              </template>
              <template #empty>
                <div class="text-center py-4">
                  <span class="text-muted" v-if="fetchFailed">Unable to load movie schedules. Please try again later.</span>
                  <span class="text-muted" v-else-if="tableItems.length === 0">No movie schedules found.</span>
                  <span class="text-muted" v-else-if="searchQuery.trim()">No movies match "{{ searchQuery.trim() }}".</span>
                  <span class="text-muted" v-else>No movies match your filters.</span>
                </div>
              </template>
              <template #cell(release_date)="data">
                {{ formatReleaseDate(data.value) }}
              </template>
              <template #cell(airing_time)="data">
                {{ formatAiringTime(data.value) }}
              </template>
              <template #cell(holiday)="data">
                {{ holidayName(data.item) }}
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>
    </b-container>

    <Footer></Footer>
  </div>
</template>

<script>
// @ is an alias to /src
import Navbar from "@/components/Navbar.vue";
import Footer from "@/components/Footer.vue";

export default {
  name: "Catalog",
  components: {
    Navbar,
    Footer,
  },
  data() {
    return {
      filters: {
        halloween: true,
        christmas: true,
        startDate: null,
        endDate: null,
        location: "all",
      },
      locationOptions: [
        { value: "all", text: "All locations" },
        { value: "national", text: "National" },
        { value: "local", text: "Local" },
      ],
      searchQuery: "",
      tableFields: [
        { key: "airing_time", label: "Showtime" },
        { key: "title", label: "Movie" },
        // { key: "release_date", label: "Release Date" },
        { key: "channel", label: "Channel" },
        { key: "holiday", label: "Holiday" },
      ],
      tableItems: [],
      isLoading: true,
      fetchFailed: false,
      halloweenCalendarID: process.env.VUE_APP_HALLOWEEN_ICAL,
      christmasCalendarID: process.env.VUE_APP_CHRISTMAS_ICAL,
      halloweenCalendarEmbed: process.env.VUE_APP_HALLOWEEN_EMBED,
      christmasCalendarEmbed: process.env.VUE_APP_CHRISTMAS_EMBED,
    };
  },
  computed: {
    isDarkMode() {
      return this.$store.getters.isDarkMode;
    },
    hasMedia() {
      return this.tableItems.length > 0;
    },
    catalogStats() {
      const stats = {
        total: this.tableItems.length,
        christmas: 0,
        halloween: 0,
        kinds: {},
      };

      this.tableItems.forEach((item) => {
        const holidayId = Number(item.holiday_id);
        if (holidayId === 1) stats.christmas += 1;
        if (holidayId === 2) stats.halloween += 1;

        const kind = String(item.kind || "Unspecified").trim() || "Unspecified";
        stats.kinds[kind] = (stats.kinds[kind] || 0) + 1;
      });

      stats.kinds = Object.keys(stats.kinds)
        .sort((first, second) => first.localeCompare(second))
        .map((name) => ({ name, count: stats.kinds[name] }));

      return stats;
    },
    filteredTableItems() {
      const query = this.searchQuery.trim().toLowerCase();
      return this.tableItems.filter((item) => {
        const holidayId = Number(item.holiday_id);
        if (holidayId === 1 && !this.filters.christmas) return false;
        if (holidayId === 2 && !this.filters.halloween) return false;

        const itemDate = this.itemDate(item);
        if (this.filters.startDate && (!itemDate || itemDate < this.filters.startDate)) {
          return false;
        }
        if (this.filters.endDate && (!itemDate || itemDate > this.filters.endDate)) {
          return false;
        }
        if (!this.matchesLocation(item)) return false;

        const searchableValues = [
          item.title,
          item.kind,
          item.description,
          item.release_date,
          item.airing_time,
          item.channel,
          item.source_name,
          this.holidayName(item),
        ];

        return !query || searchableValues.some((value) =>
          String(value || "").toLowerCase().includes(query)
        );
      });
    },
  },
  mounted() {
    this.fetchMedia();
  },
  methods: {
    itemDate(item) {
      const value = item.airing_date || item.date || item.release_date;
      return value ? String(value).slice(0, 10) : null;
    },
    matchesLocation(item) {
      const selectedLocation = this.filters.location;
      if (selectedLocation === "all") return true;

      if (typeof item.is_national === "boolean") {
        return selectedLocation === "national"
          ? item.is_national
          : !item.is_national;
      }

      const location = item.location || item.location_type || item.coverage || item.market;
      return location
        ? String(location).toLowerCase().includes(selectedLocation)
        : false;
    },
    clearSearch() {
      this.searchQuery = "";
      this.$nextTick(() => this.$refs.searchInput.focus());
    },
    holidayName(item) {
      if (Number(item.holiday_id) === 1) return "Christmas";
      if (Number(item.holiday_id) === 2) return "Halloween";
      return "—";
    },
    formatReleaseDate(value) {
      if (!value) return "—";

      const [year, month, day] = value.slice(0, 10).split("-").map(Number);
      const date = new Date(year, month - 1, day);
      if (Number.isNaN(date.getTime())) return value;

      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
    formatAiringTime(value) {
      if (!value) return "—";

      const [hours, minutes, seconds = 0] = value.split(":").map(Number);
      if ([hours, minutes, seconds].some(Number.isNaN)) return value;

      return new Date(2000, 0, 1, hours, minutes, seconds).toLocaleTimeString(
        undefined,
        { hour: "numeric", minute: "2-digit" }
      );
    },
    async fetchMedia() {
      this.isLoading = true;
      this.fetchFailed = false;
      const mediaItems = [];
      const apiBaseUrl = (process.env.VUE_APP_API_BASE_URL || "").replace(/\/+$/, "");
      let nextPage = `${apiBaseUrl}/api/media`;

      try {
        while (nextPage) {
          const response = await fetch(nextPage);
          if (!response.ok) {
            throw new Error(`Media request failed: ${response.status}`);
          }

          const data = await response.json();
          if (Array.isArray(data)) {
            mediaItems.push(...data);
            nextPage = null;
          } else {
            mediaItems.push(...(data.results || []));
            nextPage = data.next ? new URL(data.next, apiBaseUrl).toString() : null;
          }
        }

        this.tableItems = mediaItems;
        console.log("Fetched media items:", this.tableItems);
      } catch (error) {
        this.fetchFailed = true;
        console.error("Failed to fetch media:", error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
